import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import {
  PlayCircle,
  BookOpen,
  ListChecks,
  ArrowRight,
  MapPin,
  Waves,
  Building2,
  Sun,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { formatDate } from "@/lib/cn";
import { Logo } from "@/components/Logo";
import { AnimatedWaves } from "@/components/AnimatedWaves";

interface PublicAnnouncement {
  id: string;
  title: string;
  content: string;
  publishedAt: string;
  category: string;
}

export function LandingPage() {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language.startsWith("en");
  const tr = (es: string, en: string) => (isEn ? en : es);

  const { data: announcements = [] } = useQuery({
    queryKey: ["public-announcements"],
    queryFn: async () => {
      try {
        const { data } = await axios.get<PublicAnnouncement[]>("/api/v1/announcements/public");
        return data;
      } catch {
        return [];
      }
    },
  });

  return (
    <div className="min-h-screen bg-sand">
      {/* ============================ HERO ============================ */}
      <header className="relative overflow-hidden bg-hero-gradient text-white">
        <div className="absolute inset-0 bg-ocean-mist opacity-60" aria-hidden="true" />

        <div className="absolute top-12 left-12 opacity-10 text-dorado animate-drift-slow hidden md:block" aria-hidden="true">
          <Sun size={140} strokeWidth={1} />
        </div>
        <div className="absolute bottom-32 right-12 opacity-10 text-white animate-drift-slow hidden md:block" aria-hidden="true">
          <Waves size={120} strokeWidth={1} />
        </div>

        <nav className="relative max-w-6xl mx-auto px-6 py-5 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <Logo variant="icon" color="white" className="h-11" />
            <div className="leading-tight">
              <div className="font-serif text-lg tracking-wider">COSTA DORADA I</div>
              <div className="text-xs text-primary-100 tracking-widest uppercase">Dorado · Puerto Rico</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => i18n.changeLanguage(i18n.language.startsWith("es") ? "en" : "es")}
              className="text-sm hover:text-dorado-200 transition uppercase tracking-wider"
            >
              {i18n.language.startsWith("es") ? "English" : "Español"}
            </button>
            <Link
              to="/login"
              className="btn bg-white/95 text-primary-700 hover:bg-white font-medium tracking-wide shadow-lg"
            >
              {t("auth.login")}
            </Link>
          </div>
        </nav>

        <div className="relative max-w-5xl mx-auto px-6 pt-12 pb-40 md:pt-20 md:pb-52 text-center z-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-xs font-medium text-dorado-200 mb-8 border border-white/15">
            <MapPin size={12} />
            <span className="tracking-wider uppercase">Dorado del Mar · {tr("Frente al Atlántico", "Atlantic Beachfront")}</span>
          </div>

          <div className="flex justify-center mb-6">
            <Logo variant="stacked" color="white" className="text-2xl" />
          </div>

          <h1 className="font-serif text-5xl md:text-7xl font-bold leading-[1.1] tracking-tight mb-6 animate-fade-up">
            {tr("Condominio", "Condominium")}
            <span className="block text-dorado mt-1 italic font-normal">Costa Dorada I</span>
          </h1>

          <p className="text-lg md:text-xl text-primary-100/90 max-w-2xl mx-auto leading-relaxed font-light mb-10">
            {tr(
              "Una comunidad residencial junto al océano, en el corazón de Dorado. Gestión moderna y transparente para nuestros titulares.",
              "A residential community by the ocean, in the heart of Dorado. Modern, transparent management for our owners."
            )}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/guia"
              className="group inline-flex items-center gap-3 px-7 py-4 bg-dorado hover:bg-dorado-600 text-white rounded-md font-medium text-base shadow-elegant-lg transition-all hover:shadow-2xl hover:-translate-y-0.5"
            >
              <PlayCircle size={22} />
              <span className="tracking-wide">{tr("Conocer el Sistema", "Tour the System")}</span>
              <ArrowRight size={16} className="group-hover:translate-x-1 transition" />
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-7 py-4 bg-white/10 hover:bg-white/15 backdrop-blur-sm border border-white/25 text-white rounded-md font-medium text-base transition"
            >
              {tr("Acceso para Titulares", "Owner Access")} →
            </Link>
          </div>

          <p className="text-sm text-primary-100/80 mt-5">
            {tr(
              "El acceso al portal lo emite la administración del condominio.",
              "Portal access is issued by the condominium administration."
            )}
          </p>

          <p className="text-xs text-primary-200/70 mt-6 tracking-wider uppercase">
            {tr("Tour interactivo · Sin necesidad de cuenta", "Interactive tour · No account needed")}
          </p>
        </div>

        <AnimatedWaves position="bottom" tone="light" />
      </header>

      {/* ============================ STATEMENT ============================ */}
      <section className="relative max-w-5xl mx-auto px-6 py-20 text-center">
        <div className="text-dorado mb-5 inline-flex items-center gap-2 text-xs tracking-widest uppercase font-medium">
          <span className="w-8 h-px bg-dorado" />
          {tr("Nuestra Comunidad", "Our Community")}
          <span className="w-8 h-px bg-dorado" />
        </div>
        <h2 className="font-serif text-3xl md:text-4xl text-primary-800 leading-tight mb-6">
          {tr(
            "Un estilo de vida sosegado, junto al mar y a pasos del campo de golf.",
            "A serene lifestyle, by the sea and steps from the golf course."
          )}
        </h2>
        <p className="text-slate-600 max-w-2xl mx-auto text-lg leading-relaxed">
          {tr(
            "El Condominio Costa Dorada I forma parte del exclusivo Dorado del Mar, una comunidad costera en Puerto Rico reconocida por su tranquilidad, seguridad y conexión con la naturaleza.",
            "Condominio Costa Dorada I is part of exclusive Dorado del Mar, a coastal community in Puerto Rico known for its tranquility, security, and connection to nature."
          )}
        </p>
      </section>

      {/* ============================ FEATURE CARDS ============================ */}
      <section className="max-w-6xl mx-auto px-6 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              to: "/help/tour",
              icon: <PlayCircle size={32} />,
              title: tr("Tour del Sistema", "System Tour"),
              desc: tr(
                "Recorrido animado por los 6 módulos del portal en menos de un minuto.",
                "Animated walkthrough of the portal's 6 modules in under a minute."
              ),
              cta: tr("Iniciar tour", "Start tour"),
            },
            {
              to: "/help/operations",
              icon: <ListChecks size={32} />,
              title: tr("Operaciones Comunes", "Common Operations"),
              desc: tr(
                "Guías paso a paso para los flujos más utilizados por la Junta y la Administración.",
                "Step-by-step guides for the workflows most used by the Board and Administration."
              ),
              cta: tr("Explorar guías", "Explore guides"),
            },
            {
              to: "/help/screens",
              icon: <BookOpen size={32} />,
              title: tr("Pantalla por Pantalla", "Screen by Screen"),
              desc: tr(
                "Referencia detallada de cada pantalla con sus acciones, roles y consejos.",
                "Detailed reference of every screen with its actions, roles, and tips."
              ),
              cta: tr("Ver referencia", "View reference"),
            },
          ].map((c) => (
            <Link
              key={c.to}
              to={c.to}
              className="group relative bg-white rounded-xl p-8 shadow-elegant hover:shadow-elegant-lg transition-all duration-300 hover:-translate-y-1 border border-sand-200"
            >
              <div className="absolute -top-4 left-8 w-14 h-14 rounded-xl bg-gradient-to-br from-primary-700 to-primary-500 text-white flex items-center justify-center shadow-elegant">
                {c.icon}
              </div>
              <div className="pt-6">
                <h3 className="font-serif text-2xl text-primary-800 mb-2">{c.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-4">{c.desc}</p>
                <div className="inline-flex items-center gap-2 text-dorado-600 font-medium text-sm group-hover:gap-3 transition-all">
                  {c.cta} <ArrowRight size={14} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ============================ ESSENCE BAND ============================ */}
      <section className="bg-gradient-to-br from-primary-50 to-sand py-20 border-t border-b border-primary-100">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
            {[
              {
                icon: <Waves size={36} className="text-ocean-400" strokeWidth={1.5} />,
                titleEs: "Frente al océano",
                titleEn: "Oceanfront",
                descEs: "Acceso directo a la costa Atlántica de Dorado.",
                descEn: "Direct access to Dorado's Atlantic coast.",
              },
              {
                icon: <Building2 size={36} className="text-dorado-500" strokeWidth={1.5} />,
                titleEs: "Junto al golf",
                titleEn: "By the golf course",
                descEs: "A pasos del campo de golf Dorado del Mar.",
                descEn: "Steps from the Dorado del Mar golf course.",
              },
              {
                icon: <ShieldCheck size={36} className="text-primary-500" strokeWidth={1.5} />,
                titleEs: "Seguridad y privacidad",
                titleEn: "Security & privacy",
                descEs: "Vigilancia 24/7 y comunidad cerrada de titulares.",
                descEn: "24/7 surveillance and gated owners' community.",
              },
            ].map((f, i) => (
              <div key={i} className="flex flex-col items-center">
                <div className="mb-4 p-4 bg-white rounded-full shadow-elegant">{f.icon}</div>
                <h3 className="font-serif text-xl text-primary-800 mb-2">{tr(f.titleEs, f.titleEn)}</h3>
                <p className="text-slate-600 text-sm leading-relaxed max-w-xs">{tr(f.descEs, f.descEn)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================ ANNOUNCEMENTS ============================ */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <div className="text-dorado mb-3 inline-flex items-center gap-2 text-xs tracking-widest uppercase font-medium">
            <Sparkles size={12} />
            {tr("Comunicados al día", "Latest from the Board")}
          </div>
          <h2 className="font-serif text-3xl md:text-4xl text-primary-800">
            {tr("Comunicados Recientes", "Recent Announcements")}
          </h2>
        </div>

        {announcements.length === 0 ? (
          <p className="text-center text-slate-500 italic">
            {tr("No hay comunicados públicos disponibles.", "No public announcements available.")}
          </p>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {announcements.map((a) => (
              <article
                key={a.id}
                className="bg-white rounded-xl p-7 shadow-elegant border border-sand-200 hover:shadow-elegant-lg transition-shadow"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-1.5 h-8 bg-dorado rounded-full" />
                  <div className="text-xs uppercase tracking-widest text-dorado-600 font-medium">
                    {a.category}
                  </div>
                </div>
                <h3 className="font-serif text-xl text-primary-800 mb-2 leading-snug">{a.title}</h3>
                <p className="text-xs text-slate-400 mb-3 uppercase tracking-wider">
                  {formatDate(a.publishedAt, i18n.language)}
                </p>
                <p className="text-sm text-slate-700 leading-relaxed line-clamp-3">{a.content}</p>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* ============================ FOOTER ============================ */}
      <footer className="relative bg-primary-800 text-white overflow-hidden mt-12">
        <div className="relative max-w-6xl mx-auto px-6 py-12 grid grid-cols-1 md:grid-cols-3 gap-8 z-10">
          <div>
            <Logo variant="wordmark" color="white" className="mb-4" />
            <p className="text-sm text-primary-100 leading-relaxed">
              {tr(
                "Condominio residencial junto al océano en Dorado, Puerto Rico. Una comunidad gestionada con transparencia.",
                "Beachfront residential condominium in Dorado, Puerto Rico. A community managed with transparency."
              )}
            </p>
          </div>
          <div>
            <h4 className="font-serif text-dorado-300 mb-3 tracking-wider uppercase text-sm">
              {tr("Acceso", "Access")}
            </h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/login" className="hover:text-dorado-200 transition">{t("auth.login")}</Link></li>
              <li><Link to="/help" className="hover:text-dorado-200 transition">{tr("Centro de Ayuda", "Help Center")}</Link></li>
              <li><Link to="/help/tour" className="hover:text-dorado-200 transition">{tr("Tour del Sistema", "System Tour")}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-serif text-dorado-300 mb-3 tracking-wider uppercase text-sm">
              {tr("Contacto", "Contact")}
            </h4>
            <ul className="space-y-2 text-sm text-primary-100">
              <li>
                <a href="mailto:admin@costadorada1.com" className="hover:text-dorado-200 transition">
                  admin@costadorada1.com
                </a>
              </li>
              <li>Dorado, Puerto Rico</li>
            </ul>
          </div>
        </div>
        <div className="relative border-t border-primary-700/50 py-5 text-center text-xs text-primary-200 z-10">
          © {new Date().getFullYear()} Condominio Costa Dorada I · {tr("Todos los derechos reservados", "All rights reserved")}
        </div>
      </footer>
    </div>
  );
}
